'use client';

import Image from 'next/image';
import { useHero } from './HeroContext';
import { useEffect, useRef, useState } from 'react';

const getSrc = (img) => {
  if (!img) return '';
  if (typeof img === 'string') return img;
  return img.sourceUrl || img.src || img.url || '';
};

export default function HeroMedia({
  defaultImageUrl,
  gradient = 'linear-gradient(to top, rgba(30,30,35,.75) 10%, rgba(30,30,35,0) 80%)'
}) {
  const heroMediaRef = useRef(null);
  const heroImageRef = useRef(null);

  const { image, heroImages, setImage } = useHero();
  const activeImage = image || defaultImageUrl;
  const activeImageRef = useRef(activeImage);
  const [transitionClass, setTransitionClass] = useState('');

  useEffect(() => {
    activeImageRef.current = activeImage;
  }, [activeImage]);

  useEffect(() => {
    if (!heroImages || heroImages.length < 2) {
      return;
    }
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let swapTimeout;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let fadeInTimeout;
    const interval = setInterval(() => {
      setTransitionClass('Hero__image--transition');

      swapTimeout = setTimeout(() => {
        const activeSrc = getSrc(activeImageRef.current);
        const currentIndex = heroImages.findIndex((img) => getSrc(img) === activeSrc);
        const nextIndex = (currentIndex + 1) % heroImages.length;
        const nextImage = heroImages[nextIndex];
        if (nextImage) {
          setImage(nextImage);
        }
        fadeInTimeout = setTimeout(() => {
          setTransitionClass('');
        }, 50);
      }, 300);
    }, 5000);
    return () => {
      clearInterval(interval);
      clearTimeout(swapTimeout);
      clearTimeout(fadeInTimeout);
    };
  }, [heroImages, setImage]);

  useEffect(() => {
    let frameId;
    const handleScroll = () => {
      frameId = requestAnimationFrame(() => {
        if (heroMediaRef.current) {
          heroMediaRef.current.style.transform = `translateY(${window.scrollY * 0.5}px)`;
        }
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(frameId);
    };
  }, []);

  // Efter hookarna: en tidig return före dem bryter mot rules-of-hooks.
  if (!activeImage) {
    return <div className="Hero__media Hero__media--empty"></div>;
  }

  const src = getSrc(activeImage);
  const activeGradient = activeImage?.gradient || gradient;

  return (
    <div
      ref={heroMediaRef}
      className={`Hero__media ${transitionClass ? 'Hero__media--transition' : ''}`.trim()}
      // @ts-ignore
      style={{ '--overlay': activeGradient }}
    >
      <Image
        src={src}
        alt={activeImage?.altText || ''}
        fill
        priority
        sizes="100vw"
        className={transitionClass}
        ref={heroImageRef}
      />
    </div>
  );
}
